'use client';

import type { AriaRole, ReactNode } from 'react';

type SettingsNoteProps = {
  children: ReactNode;
  /**
   * Draws the subtle bordered container the prototype uses for standing notes
   * (`.dl-note`). Leave off for transient copy like a loading line.
   */
  boxed?: boolean;
  /**
   * Passed through so callers keep control of the announcement contract —
   * `status` for loading, `note` for a standing advisory, nothing for plain copy.
   */
  role?: AriaRole;
  className?: string;
  'data-testid'?: string;
};

/**
 * Small supporting copy on a settings surface: read-only advisories, loading
 * lines, "nothing configured yet" messages.
 *
 * DS-GAP(Alert as plain supporting copy): DS `Alert` (0.30.12) takes `role="alert" | "status"`,
 * but it is a coloured, iconed callout and cannot render a plain `<p>` with the `note` role or none.
 * Delete this when the DS has a plain note / supporting-text component.
 *
 * Deliberately not a DS `Alert`: this is quiet copy that is simply present on the page, not a
 * callout.
 */
export const SettingsNote = ({
  children,
  boxed = false,
  role,
  className,
  'data-testid': dataTestId,
}: SettingsNoteProps) => (
  <p
    data-lula-ds=""
    role={role}
    data-testid={dataTestId}
    className={`typography-body-sm m-0 text-text-secondary${
      boxed
        ? ' rounded-md border border-solid border-border-subtle bg-bg-subtle px-space-12 py-space-8'
        : ''
    }${className ? ` ${className}` : ''}`}
  >
    {children}
  </p>
);

export default SettingsNote;
